import { NetworkStatus } from "@apollo/client";
import useLocalStorage from "@rehooks/local-storage";
import { isEqual } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";

import usePolledQuery from "apollo/usePolledQuery";
import FlashContext from "components/FlashMessages/FlashContext";
import usePrevious from "hooks/usePrevious";
import useTypedContext from "hooks/useTypedContext";
import { SearchQueryRequestedPage } from "types/generated";

import { PollingIntervalGroups } from "../../../apollo/constants";
import { INITIAL_STACKS_LIMIT, STACK_LIMIT_STORAGE_KEY } from "./constants";
import { SEARCH_STACKS } from "./gql";
import useFiltersVariables from "./useFiltersVariables";
import useStacksTableSelectionStore from "./Table/useStacksTableSelectionStore";

const useStacks = (paginationOptions: number[]) => {
  const [rowsPerPage, setRowsPerPage] = useLocalStorage<number>(STACK_LIMIT_STORAGE_KEY);
  const [requestedPage, setRequestedPage] = useState<SearchQueryRequestedPage | null>(null);
  const [after, setAfter] = useState<string | null>(null);

  const clearSelected = useStacksTableSelectionStore((state) => state.clearSelected);

  // Sync current rows per page with pagination options as they can be changed by FF
  useEffect(() => {
    if (rowsPerPage && !paginationOptions.includes(rowsPerPage)) {
      setRowsPerPage(INITIAL_STACKS_LIMIT);
    }
  }, [paginationOptions, rowsPerPage, setRowsPerPage]);

  const first = useMemo(() => {
    if (rowsPerPage && paginationOptions.includes(rowsPerPage)) {
      return rowsPerPage;
    }

    return INITIAL_STACKS_LIMIT;
  }, [paginationOptions, rowsPerPage]);

  const { onError } = useTypedContext(FlashContext);

  const { filterVariables, filterVariablesLoaded } = useFiltersVariables();

  const resetPaginationVariables = useMemo(() => {
    return {
      fullTextSearch: filterVariables.searchInput,
      predicates: filterVariables.predicates,
      ...(filterVariables.sortOptionFields && { orderBy: filterVariables.sortOptionFields }),
    };
  }, [filterVariables]);

  const previousResetPaginationVariables = usePrevious(resetPaginationVariables);

  // Reset selection when variables changed
  useEffect(() => {
    if (!isEqual(previousResetPaginationVariables, resetPaginationVariables)) {
      clearSelected();
      setRequestedPage(null);
      setAfter(null);
    }
  }, [previousResetPaginationVariables, resetPaginationVariables, clearSelected]);

  const initialVariables = useMemo(() => {
    return {
      input: {
        ...resetPaginationVariables,
        first,
        after,
        requestedPage,
      },
    };
  }, [resetPaginationVariables, first, after, requestedPage]);

  const { error, loading, data, networkStatus, previousData, refetch } = usePolledQuery(
    SEARCH_STACKS,
    {
      variables: initialVariables,
      pollingGroup: PollingIntervalGroups.Lists,
      // avoid request executing twice while fetchMore
      nextFetchPolicy: "cache-first",
      skip: !filterVariablesLoaded,
    }
  );

  const loadStacks = useCallback(
    async ({
      cursor,
      requestedPage,
    }: {
      cursor: string | null;
      rowsPerPage: number;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      try {
        setAfter(cursor);
        setRequestedPage(requestedPage);
      } catch (error) {
        onError(error);
      }
    },
    [onError]
  );

  const initialLoading =
    (loading && networkStatus === NetworkStatus.loading && !data?.searchStacks) ||
    !filterVariablesLoaded;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const stacks = useMemo(
    () =>
      data?.searchStacks?.edges.map((edge) => edge.node) ||
      previousData?.searchStacks?.edges.map((edge) => edge.node) ||
      [],
    [data?.searchStacks?.edges, previousData?.searchStacks?.edges]
  );

  return {
    refetch,
    loadStacks,
    error,
    stacks,
    initialLoading,
    noResults: !loading && !data?.searchStacks,
    endCursor: data?.searchStacks?.pageInfo.endCursor,
    startCursor: data?.searchStacks?.pageInfo.startCursor,
    isEmpty: data && !stacks.length,
    loadingContent,
    predicates: filterVariables.predicates,
  };
};

export default useStacks;
